import React, {useEffect, useState} from "react";
import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {getCourierAssignmentHistory, getCourierOrderTimeline} from "../data-tables-distributor/orders/courier-api";
import {formatDateNow} from "../i18n/date-formatting";
import DeliveryProgressChip from "./delivery-progress-chip";
import DeliveryTimeline from "./delivery-timeline";
import {isDeliveryTimelineEvent} from "./delivery-progress";

export default function CourierOrderHistory({orderId}) {
    const {t, i18n} = useTranslation();
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState({loading: true, events: [], error: ""});
    const language = i18n.language;
    const scope = `${orderId}:${language}:${retry}`;
    useEffect(() => {
        const controller = new AbortController();
        setResult({scope, loading: true, events: [], error: ""});
        Promise.all([getCourierOrderTimeline(orderId, language, controller.signal),
            getCourierAssignmentHistory(orderId, language, controller.signal)]).then(([events, history]) => {
            if (!controller.signal.aborted) setResult({scope, loading: false, events: Array.isArray(events) ? events : [],
                history: Array.isArray(history) ? history : [], error: ""});
        }).catch(error => {
            if (!controller.signal.aborted) setResult({scope, loading: false, events: [],
                error: error.response?.data?.message || "CourierDeliveryHistoryLoadFailed"});
        });
        return () => controller.abort();
    }, [orderId, language, retry, scope]);
    const current = result.scope === scope ? result : {loading: true, events: [], error: ""};
    return <Box sx={{p: 2}}>
        {current.loading ? <CircularProgress size={24} aria-label={t("Loading")}/> : current.error ?
            <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>{t("Retry")}</Button>}>
                {t(current.error)}</Alert> : <>
                {current.history?.length > 0 && <Box>
                    <Typography variant="subtitle2">{t("CourierAssignmentHistory")}</Typography>
                    {current.history.map(assignment => <Box key={assignment.id} sx={{py: 1, borderBottom: 1, borderColor: "divider"}}>
                        <Typography fontWeight={600}>{assignment.previousCourierProfileId
                            ? `${assignment.previousCourierName?.trim() || assignment.previousCourierProfileId} → ` : ""}
                            {assignment.courierName?.trim() || assignment.courierProfileId}</Typography>
                        <Typography variant="caption">{formatDateNow(assignment.assignedAt, language, true)}</Typography>
                        <Typography variant="body2">{t("CourierAssignmentActor")}: {assignment.actorName?.trim() || assignment.assignedBy}</Typography>
                        {assignment.reason && <Typography variant="body2" sx={{whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}>
                            {t("CourierReassignmentReason")}: {assignment.reason}</Typography>}
                        <DeliveryProgressChip status={assignment.deliveryStatus}/>
                        {assignment.releasedAt && <Typography variant="caption" display="block">
                            {t("CourierAssignmentClosed")}: {formatDateNow(assignment.releasedAt, language, true)}</Typography>}
                    </Box>)}
                </Box>}
                {current.events.some(isDeliveryTimelineEvent) ? <DeliveryTimeline events={current.events}/>
                    : !current.history?.length && <Typography color="text.secondary">{t("CourierDeliveryHistoryEmpty")}</Typography>}
            </>}
    </Box>;
}
